import { Controller } from "stimulus";

// Spec: docs/specs/2026-09-16-shp-18424-cortana-duplicate-upload-confirmation.md
//
// Pre-flight duplicate check for the Cortana "New document" upload form.
// Hashes the selected file in the browser (same SHA-256 the backend stores)
// and asks the server whether it already matches a document on this
// shipment, before the real (unchanged) form submit happens.
export default class extends Controller {
  static targets = ["fileInput", "banner", "bannerMessage", "submitButton"];
  static values = { shipmentId: Number };

  async check() {
    const file = this.fileInputTarget.files[0];
    if (!file) {
      this.hideBanner();
      return;
    }

    if (!window.crypto || !window.crypto.subtle) {
      console.warn(
        "crypto.subtle is not available (requires a secure context: HTTPS or localhost)"
      );
      return;
    }

    let hash;
    try {
      hash = await this.hashFile(file);
    } catch (error) {
      console.error("could not hash file for duplicate check", error);
      return;
    }

    // A fast second file selection can resolve here first; only the response
    // for the file still in the input should be allowed to touch the banner.
    if (this.fileInputTarget.files[0] !== file) return;

    try {
      const response = await fetch(
        `/admin/shipments/${this.shipmentIdValue}/shipment_documents/check_duplicate`,
        {
          method: "POST",
          headers: this.jsonHeaders(),
          credentials: "same-origin",
          body: JSON.stringify({ sha256_hash: hash }),
        }
      );
      if (this.fileInputTarget.files[0] !== file) return;

      const body = await response.json().catch(() => ({}));
      if (this.fileInputTarget.files[0] !== file) return;

      if (body.duplicate) {
        this.showBanner(body.existing_documents);
      } else {
        this.hideBanner();
      }
    } catch (error) {
      console.error("check_duplicate request failed", error);
      if (this.fileInputTarget.files[0] === file) this.hideBanner();
    }
  }

  async hashFile(file) {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  // existingDocuments can hold more than one match (multiple prior uploads
  // already share this file's content), so each gets its own line.
  showBanner(existingDocuments) {
    this.bannerMessageTarget.textContent = "";
    existingDocuments.forEach((doc) => {
      const line = document.createElement("p");
      line.className = "mb-1";
      line.textContent = this.describeDuplicate(doc);
      this.bannerMessageTarget.appendChild(line);
    });
    this.bannerTarget.classList.remove("d-none");
    this.submitButtonTarget.classList.add("d-none");
  }

  describeDuplicate(existingDocument) {
    const types = existingDocument.document_types.length
      ? ` (${existingDocument.document_types.join(", ")})`
      : "";
    const containers = existingDocument.containers.join(", ");
    const filename = existingDocument.filename || "unnamed file";
    const parts = [`A document matching "${filename}"${types} already exists on this shipment`];
    if (containers) parts.push(`for container(s) ${containers}`);
    if (existingDocument.booking_id) parts.push(`under booking ${existingDocument.booking_id}`);
    return `${parts.join(", ")}, uploaded by ${existingDocument.uploaded_by || "unknown"} on ${
      existingDocument.uploaded_at
    }.`;
  }

  hideBanner() {
    this.bannerTarget.classList.add("d-none");
    this.submitButtonTarget.classList.remove("d-none");
  }

  uploadAnyway() {
    this.hideBanner();
    if (this.element.requestSubmit) {
      this.element.requestSubmit();
    } else {
      this.element.submit();
    }
  }

  cancel() {
    this.fileInputTarget.value = "";
    this.hideBanner();
  }

  jsonHeaders() {
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const token = document.querySelector('meta[name="csrf-token"]');
    if (token) headers["X-CSRF-Token"] = token.getAttribute("content");
    return headers;
  }
}
